import { TableColumnsType } from 'antd';
import { TierBadge, GenericBadge } from '../../dataInputs/tiers/components';
import { renderComplianceBadges } from '../../cyber/utils/complianceBadgeRenderer';
import { CollapsibleText } from '../../../components/generic/TextTruncation';
import {
  ComplianceStatusEnum,
  WorkaroundDefinedType,
} from '../../../shared/types/business-functions-workarounds';
import { CompletionPercentage } from '../../cyber/components';
import {
  CyberHubApplicationData,
  TierData,
  WorkaroundData,
  WorkaroundTimelineCompliance,
} from '../types/it-applications';
import { APPLICATION_TYPES } from '../../cyber/constants';

const COLUMN_WIDTHS = {
  APPLICATION_NAME: 150,
  APPLICATION_TYPE: 100,
  DR_TIER: 100,
  WORKAROUND_COMPLIANCE: 250,
  WORKAROUND_COMPLETION: 100,
  RUNTIME_HOURS: 100,
  MAX_DATA_LOSS: 100,
  COMMENT: 450,
  ARROW_LINK: 50,
} as const;

const CSS_CLASSES = {
  PRIMARY_MEDIUM_TEXT: 'text-primary-100 font-medium',
} as const;

// Color constants for application type badges (using central APPLICATION_TYPES)
const APPLICATION_TYPE_COLORS: Record<string, string> = {
  [APPLICATION_TYPES.ENTERPRISE]: '#437EF7', // Blue for Enterprise applications
  [APPLICATION_TYPES.LOCATIONS]: '#DF9000', // Orange for Location-specific applications
};

const convertComplianceData = (
  complianceData: WorkaroundTimelineCompliance,
): Record<string, ComplianceStatusEnum> => {
  return {
    slaCompliant: complianceData.slaCompliant as ComplianceStatusEnum,
    rtoCompliant: complianceData.rtoCompliant as ComplianceStatusEnum,
    mtoCompliant: complianceData.mtoCompliant as ComplianceStatusEnum,
    rpoCompliant: complianceData.rpoCompliant as ComplianceStatusEnum,
  };
};

const formatRuntimeHours = (workaround: WorkaroundData): string => {
  if (workaround.workaroundDefined === WorkaroundDefinedType.NO_WORKAROUND) {
    return 'N/A';
  }
  if (workaround.workaroundDefined === WorkaroundDefinedType.INFINITE) {
    return 'Infinite';
  }
  return `${workaround.runtimeHours || 0} hours`;
};

const formatDataLossHours = (workaround: WorkaroundData): string => {
  return `${workaround.maxDataLossHours || 0} hours`;
};

const renderApplicationName = (name: string) => (
  <span className={`${CSS_CLASSES.PRIMARY_MEDIUM_TEXT} text-black`}>
    {name}
  </span>
);

const renderApplicationType = (applicationType: string) => {
  const badgeColor = APPLICATION_TYPE_COLORS[applicationType] || '#437EF7';
  return (
    <GenericBadge name={applicationType} color={badgeColor} showDot={false} />
  );
};

const renderTier = (tier: TierData | null) => {
  if (!tier) {
    return (
      <span className={CSS_CLASSES.PRIMARY_MEDIUM_TEXT}>Not Provided</span>
    );
  }

  return (
    <TierBadge
      color={tier.color}
      name={tier.name}
      className={CSS_CLASSES.PRIMARY_MEDIUM_TEXT}
    />
  );
};

const renderWorkaroundCompliance = (workaround: WorkaroundData) => {
  return renderComplianceBadges(
    convertComplianceData(workaround.workaroundTimelineCompliance),
    {
      isWorkaroundNeeded: workaround.isWorkaroundNeeded,
    },
  );
};

const renderWorkaroundCompletion = (
  _value: any,
  record: CyberHubApplicationData,
) => {
  return <CompletionPercentage percent={record.completionPercentage} />;
};

const renderRuntimeHours = (workaround: WorkaroundData) => {
  const runtime = formatRuntimeHours(workaround);

  return <span className={CSS_CLASSES.PRIMARY_MEDIUM_TEXT}>{runtime}</span>;
};

const renderMaxDataLoss = (workaround: WorkaroundData) => {
  const dataLossTime = formatDataLossHours(workaround);
  return (
    <span className={CSS_CLASSES.PRIMARY_MEDIUM_TEXT}>{dataLossTime}</span>
  );
};

const renderBusinessFunctionItApplicationComment = (
  _value: any,
  record: CyberHubApplicationData,
) => (
  <CollapsibleText
    text={record.comment || 'N/A'}
    className={CSS_CLASSES.PRIMARY_MEDIUM_TEXT}
  />
);

const createMultiLineTitle = (lines: string[]) => (
  <span>
    {lines.map((line, index) => (
      <span key={index}>
        {line}
        {index < lines.length - 1 && <br />}
      </span>
    ))}
  </span>
);

export const columns: TableColumnsType<CyberHubApplicationData> = [
  {
    title: 'Application Name',
    dataIndex: 'name',
    key: 'name',
    width: COLUMN_WIDTHS.APPLICATION_NAME,
    render: renderApplicationName,
  },
  {
    title: 'Application Type',
    dataIndex: 'applicationType',
    key: 'applicationType',
    width: COLUMN_WIDTHS.APPLICATION_TYPE,
    align: 'center',
    render: renderApplicationType,
  },
  {
    title: 'DR Tier',
    dataIndex: 'tier',
    key: 'tier',
    width: COLUMN_WIDTHS.DR_TIER,
    align: 'center',
    render: renderTier,
  },
  {
    title: createMultiLineTitle(['Workaround', 'Timeline', 'Compliance']),
    dataIndex: 'workaround',
    key: 'workaroundTimelineCompliance',
    align: 'center',
    width: COLUMN_WIDTHS.WORKAROUND_COMPLIANCE,
    render: renderWorkaroundCompliance,
  },
  {
    title: 'Workaround Completion',
    dataIndex: 'workaround',
    key: 'workaroundCompletion',
    align: 'center',
    width: COLUMN_WIDTHS.WORKAROUND_COMPLETION,
    render: renderWorkaroundCompletion,
  },
  {
    title: createMultiLineTitle(['Runtime with', 'Workaround']),
    dataIndex: 'workaround',
    key: 'runtimeHours',
    align: 'center',
    width: COLUMN_WIDTHS.RUNTIME_HOURS,
    render: renderRuntimeHours,
  },
  {
    title: createMultiLineTitle(['Max Data', 'Loss']),
    dataIndex: 'workaround',
    key: 'maxDataLossHours',
    align: 'center',
    width: COLUMN_WIDTHS.MAX_DATA_LOSS,
    render: renderMaxDataLoss,
  },
  {
    title: 'Why is this application important to the Function?',
    dataIndex: 'comment',
    key: 'comment',
    width: COLUMN_WIDTHS.COMMENT,
    align: 'left',
    render: renderBusinessFunctionItApplicationComment,
  },
  {
    title: '',
    dataIndex: 'arrowLink',
    key: 'arrowLink',
    width: COLUMN_WIDTHS.ARROW_LINK,
    align: 'center',
    render: () => {
      // TODO: Implement link rendering logic once the page is available
      return <span />;
    },
  },
];
